import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Typography } from "@mui/material";
import { useState } from "react";

import {
  AgentPerformanceData,
  RiskAnalysisData
} from "../../../../types/risk-analysis";
import { getRiskLevelColor } from "./constants";
import MetricPill from "./MetricPill";
import SummaryFooter from "./SummaryFooter";

interface OverallRiskCardProps {
  riskAnalysis: RiskAnalysisData;
  agentPerformance?: AgentPerformanceData;
}

const OverallRiskCard = ({
  riskAnalysis,
  agentPerformance
}: OverallRiskCardProps) => {
  const [expandedMetrics, setExpandedMetrics] = useState<Record<string, boolean>>({});

  const riskColor = getRiskLevelColor(riskAnalysis.overallIndicator);
  const isAnyMetricExpanded =
    expandedMetrics.severity ||
    expandedMetrics.likelihood ||
    expandedMetrics.timeSensitivity;

  const toggleMetric = (metric: string) => {
    setExpandedMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };

  return (
    <Box
      sx={{
        bgcolor: "white",
        border: "1px solid #e5e7eb",
        borderLeft: `4px solid ${riskColor}`,
        borderRadius: "8px",
        overflow: "hidden",
        mb: 3
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 400px", p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                bgcolor: riskColor,
                borderRadius: "9999px",
                p: "6px 14px 6px 6px",
                display: "flex",
                alignItems: "center",
                gap: 0.75
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                  borderRadius: "50%",
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <WarningAmberIcon sx={{ color: "white", fontSize: 14 }} />
              </Box>
              <Typography
                sx={{ fontSize: "12px", fontWeight: 600, color: "white" }}
              >
                {riskAnalysis.overallIndicator}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}>
              Overall Risk
            </Typography>
          </Box>

          <Box sx={{ pt: 2, borderTop: "1px solid #e5e7eb" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#374151", mb: 1 }}
            >
              Key Risk Factors:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {riskAnalysis.evidence.map((reason, idx) => (
                <Box
                  key={idx}
                  sx={{
                    fontSize: "14px",
                    color: "#4b5563",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ color: riskColor, mt: "2px", fontSize: "14px" }}
                  >
                    {"•"}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: "14px", color: "inherit" }}
                  >
                    {reason}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: isAnyMetricExpanded ? "0 0 355px" : "0 0 auto",
            minWidth: isAnyMetricExpanded ? "355px" : "170px",
            borderLeft: "1px solid #e5e7eb",
            bgcolor: "#fafafa",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            transition: "all 0.3s ease"
          }}
        >
          <MetricPill
            label="Severity"
            score={riskAnalysis.axes.severity.score}
            justification={riskAnalysis.axes.severity.justification}
            isExpanded={Boolean(expandedMetrics.severity)}
            onToggle={() => toggleMetric("severity")}
            icon={<TrendingUpIcon sx={{ color: "#6b7280", fontSize: 14 }} />}
          />
          <MetricPill
            label="Likelihood"
            score={riskAnalysis.axes.likelihood.score}
            justification={riskAnalysis.axes.likelihood.justification}
            isExpanded={Boolean(expandedMetrics.likelihood)}
            onToggle={() => toggleMetric("likelihood")}
            icon={<TrackChangesIcon sx={{ color: "#6b7280", fontSize: 14 }} />}
          />
          <MetricPill
            label="Time Sensitive"
            score={riskAnalysis.axes.timeSensitivity.score}
            justification={riskAnalysis.axes.timeSensitivity.justification}
            isExpanded={Boolean(expandedMetrics.timeSensitivity)}
            onToggle={() => toggleMetric("timeSensitivity")}
            icon={<AccessTimeIcon sx={{ color: "#6b7280", fontSize: 14 }} />}
          />
        </Box>
      </Box>

      <SummaryFooter
        riskAnalysis={riskAnalysis}
        agentPerformance={agentPerformance}
      />
    </Box>
  );
};

export default OverallRiskCard;
